import { Controller } from "@hotwired/stimulus"

// Single Location Search Controller for event location selection
// Connects to data-controller="single-location-search"
export default class extends Controller {
  static targets = ["input", "suggestions", "hiddenInput", "loading", "display"]
  static values = { 
    selected: String,
    url: String 
  }

  connect() {
    // Initialize selected value
    this.selectedLocation = this.selectedValue || ''
    
    // Debounced search function
    this.searchTimeout = null
    
    // Current keyboard selection index
    this.currentSelection = -1
    
    // Update display
    this.updateDisplay()
    this.updateHiddenInput()
    
    // Close suggestions when clicking outside
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener('click', this.boundCloseOnClickOutside)
  }

  disconnect() {
    
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    document.removeEventListener('click', this.boundCloseOnClickOutside)
  }

  search(event) {
    const query = event.target.value.trim()
    
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    // Reset keyboard selection
    this.currentSelection = -1
    
    if (query.length < 2) {
      this.hideSuggestions()
      return
    }
    
    // Show loading indicator
    this.showLoading()
    
    // Debounce the search
    this.searchTimeout = setTimeout(() => {
      this.fetchSuggestions(query)
    }, 300)
  }

  async fetchSuggestions(query) {
    try {
      // Build URL with query parameter, preserving existing parameters
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('q', query)
      
      const response = await fetch(url.toString())
      const suggestions = await response.json()
      
      this.displaySuggestions(suggestions)
      this.hideLoading()
    } catch (error) {
      console.error('Failed to fetch location suggestions:', error)
      this.hideLoading()
      this.hideSuggestions()
    }
  }

  displaySuggestions(suggestions) {
    if (!this.hasSuggestionsTarget) return
    
    if (suggestions.length === 0) {
      this.hideSuggestions()
      return
    }
    
    const html = suggestions.map((suggestion, index) => {
      const icon = suggestion.type === 'province' ? 'ph-map-trifold' : 'ph-buildings'
      const colorClass = suggestion.type === 'province' ? 'text-blue-600' : 'text-green-600'

      return `
        <div class="suggestion-item px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between border-b border-gray-100 last:border-b-0"
             data-index="${index}"
             data-value="${suggestion.value}"
             data-label="${suggestion.label}"
             data-action="click->single-location-search#selectSuggestion">
          <div class="flex items-center">
            <i class="ph-fill ${icon} ${colorClass} mr-3 w-4"></i>
            <div>
              <div class="text-sm font-medium text-gray-900">${suggestion.label}</div>
              <div class="text-xs text-gray-500">${suggestion.description}</div>
            </div>
          </div>
          <i class="ph-fill ph-check text-gray-400 text-xs"></i>
        </div>
      `
    }).join('')
    
    this.suggestionsTarget.innerHTML = html
    this.showSuggestions()
  }

  selectSuggestion(event) {
    event.preventDefault()
    
    const suggestionElement = event.currentTarget
    const value = suggestionElement.dataset.value
    const label = suggestionElement.dataset.label
    
    if (value) {
      this.selectedLocation = value
      this.updateDisplay()
      this.updateHiddenInput()
    }
    
    // Clear input and hide suggestions
    this.inputTarget.value = ''
    this.hideSuggestions()
  }

  clearLocation(event) {
    event.preventDefault()
    
    this.selectedLocation = ''
    this.updateDisplay()
    this.updateHiddenInput()
  }

  updateDisplay() {
    if (!this.hasDisplayTarget) return
    
    if (!this.selectedLocation) {
      this.displayTarget.innerHTML = ''
      return
    }
    
    // Check if it's a province (no comma) or city (has comma)
    const isProvince = !this.selectedLocation.includes(', ')
    const colorClass = isProvince ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
    const icon = isProvince ? 'ph-map-trifold' : 'ph-buildings'

    const html = `
      <div class="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium ${colorClass}">
        <i class="ph-fill ${icon} mr-2"></i>
        ${this.selectedLocation}
        <button type="button"
                class="ml-2 text-current hover:text-opacity-75"
                data-action="click->single-location-search#clearLocation">
          <i class="ph-bold ph-x text-xs"></i>
        </button>
      </div>
    `
    
    this.displayTarget.innerHTML = html
  }

  updateHiddenInput() {
    if (!this.hasHiddenInputTarget) return
    
    this.hiddenInputTarget.value = this.selectedLocation
  }

  showSuggestions() {
    if (this.hasSuggestionsTarget) {
      this.suggestionsTarget.classList.remove('hidden')
    }
  }

  hideSuggestions() {
    if (this.hasSuggestionsTarget) {
      this.suggestionsTarget.classList.add('hidden')
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideSuggestions()
    }
  }

  // Keyboard navigation support
  keydown(event) {
    if (!this.hasSuggestionsTarget || this.suggestionsTarget.classList.contains('hidden')) {
      return
    }
    
    const suggestions = this.suggestionsTarget.querySelectorAll('.suggestion-item')
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.currentSelection = Math.min(this.currentSelection + 1, suggestions.length - 1)
        this.updateKeyboardSelection(suggestions)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.currentSelection = Math.max(this.currentSelection - 1, -1)
        this.updateKeyboardSelection(suggestions)
        break
      case 'Enter':
        event.preventDefault()
        if (this.currentSelection >= 0 && suggestions[this.currentSelection]) {
          suggestions[this.currentSelection].click()
        }
        break
      case 'Escape':
        event.preventDefault()
        this.hideSuggestions()
        this.inputTarget.blur()
        break
    }
  }

  updateKeyboardSelection(suggestions) {
    // Remove previous selection
    suggestions.forEach(item => item.classList.remove('bg-blue-50'))
    
    // Add selection to current item
    if (this.currentSelection >= 0 && suggestions[this.currentSelection]) {
      suggestions[this.currentSelection].classList.add('bg-blue-50')
    }
  }
}